// DEPENDENCIES
import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import _ from 'lodash';
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
// COMPONENTS
import { Link } from "react-router-dom";
import InputMask from "react-input-mask";
import { ModalProgressBar } from "../../../_metronic/_partials/controls";
import SVG from "react-inlinesvg";
import DatePicker from 'react-datepicker';
import MaskedInput from 'react-maskedinput';
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
// CUSTOM COMPONENTS
import * as alert from "../../../redux/alertSnackBarRedux";
// SERVICES AND HELPERS
import { getInputClasses, convertJsonToFormData, formatToUSDate } from "../../../helpers/helper";
import UserRegisterValidator from '../../../helpers/validators/UserRegisterValidator';
import { registerUser } from "../../../services/management/userService";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const UserRegisterPage = (props) => {
    const [loading, setloading] = useState(false);
    const [pic, setPic] = useState(toAbsoluteUrl("/media/users/blank.png"));
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [userRegisterError, setUserRegisterError] = useState("");
    const [signatureFile, setSignatureFile] = useState([]);

    const imageUploadRef = useRef();

    const initialValues = {
        imageUrl: '',
        signatureImage: '',
        firstname: '',
        lastname: '',
        email: '',
        username: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        isActive: true,
        ssn: '',
        hourlyRate: '',
        payloadMultiplier: null,
        roles: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        dateOfBirth: '',
        TWICPin: '',
        safetyCouncilStudentId: '',
        driverLicenseNumber: '',
        DISAId: '',
        AIId: '',
    };

    const formik = useFormik({
        initialValues,
        validationSchema: UserRegisterValidator,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            registerUserHandler(values, setStatus, setSubmitting);
        },
        onReset: (values, { resetForm }) => {
            resetForm();
        },
    });

    const getUserPic = () => {
        if (!pic) {
            return "none";
        }

        return `url(${pic})`;
    };

    const removePic = () => {
        setPic(null);
    };

    const previewAvatar = () => {
        if (imageUploadRef.current.files.length > 0) {
            var reader = new FileReader();
            reader.onload = function (e) {
                setPic(e.target.result);
            };
            reader.readAsDataURL(imageUploadRef.current.files[0]);
        }
    }

    const handleRoleChange = (e) => {
        var selectedRolesCopy = [...selectedRoles];
        if (e.target.checked) {
            selectedRolesCopy.push(e.target.name);
        } else {
            _.pull(selectedRolesCopy, e.target.name);
        }

        setSelectedRoles(selectedRolesCopy);
    }

    const registerUserHandler = (values, setStatus, setSubmitting) => {
        var errorMsg = "";
        setUserRegisterError("");
        if (selectedRoles.length > 0) {
            let trimmedSSN = values.ssn.replaceAll('-', '').replaceAll('_', '');
            if (trimmedSSN === "" || trimmedSSN.length === 9) {
                setSubmitting(true);
                setloading(true);
                values.roles = selectedRoles.join(',');
                values.imageUrl = imageUploadRef.current.files.length > 0 ? imageUploadRef.current.files[0] : null;
                values.signatureImage = signatureFile.length > 0 ? signatureFile[0].file : null;
                registerUser(convertJsonToFormData(values)).then(res => {
                    props.history.push("/management/users");
                    props.showAlert({ message: res.message, variant: "success" });
                }).catch(ex => {
                    setUserRegisterError(ex.message);
                    setSubmitting(false);
                    setloading(false);
                });
                return;
            }
            errorMsg = "Please enter valid ssn or you can leave the field empty.";
        } else {
            errorMsg = "Please select at least one role to be assigned to this user.";
        }

        setUserRegisterError(errorMsg);
        setSubmitting(false);
        setloading(false);
    };

    return (
        <form
            className="card card-custom card-stretch"
            onSubmit={formik.handleSubmit}
        >
            {loading && <ModalProgressBar />}
            <div className="card-header py-3">
                <div className="card-title align-items-start flex-column">
                    <h3 className="card-label font-weight-bolder text-dark">
                        Add User
                    </h3>
                    <span className="text-muted font-weight-bold fz-xs mt-1">
                        Please fill out the form below. Fields marked as <code>*</code> are required.
                    </span>
                </div>
                <div className="card-toolbar">
                    <button
                        type="submit"
                        className={`btn btn-success mr-2`}
                        disabled={
                            formik.isSubmitting || (formik.touched && !formik.isValid)
                        }
                    >
                        Save User
                        {formik.isSubmitting}
                    </button>
                    <Link
                        to="/management/users"
                        className="btn btn-danger"
                    >
                        Cancel
                    </Link>
                </div>
            </div>
            <div className="form">
                <div className="card-body">
                    {
                        userRegisterError ?
                            <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                                <div className="alert-text font-weight-bold">
                                    {userRegisterError}
                                </div>
                            </div>
                            : <></>
                    }
                    <div className="row">
                        <label className="col-xl-3"></label>
                        <div className="col-lg-9 col-xl-6">
                            <h5 className="mb-6 fz-md fw-700 c-primary">Personal Info</h5>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">Avatar</label>
                        <div className="col-lg-9 col-xl-6">
                            <div
                                className="image-input image-input-outline"
                                id="kt_profile_avatar"
                            >
                                <div
                                    className="image-input-wrapper background-image-container"
                                    style={{ backgroundImage: `${getUserPic()}` }}
                                />
                                <label
                                    className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
                                    data-action="change"
                                    data-toggle="tooltip"
                                    title=""
                                    data-original-title="Change avatar"
                                >
                                    <i className="fa fa-pen icon-sm text-muted"></i>
                                    <input
                                        type="file"
                                        ref={imageUploadRef}
                                        accept=".png, .jpg, .jpeg"
                                        onChange={previewAvatar}
                                    />
                                    <input type="hidden" name="profile_avatar_remove" />
                                </label>
                                <span
                                    className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
                                    data-action="cancel"
                                    data-toggle="tooltip"
                                    title=""
                                    data-original-title="Cancel avatar"
                                >
                                    <i className="ki ki-bold-close icon-xs text-muted"></i>
                                </span>
                                <span
                                    onClick={removePic}
                                    className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
                                    data-action="remove"
                                    data-toggle="tooltip"
                                    title=""
                                    data-original-title="Remove avatar"
                                >
                                    <i className="ki ki-bold-close icon-xs text-muted"></i>
                                </span>
                            </div>
                            <span className="form-text text-muted">
                                Allowed file types: png, jpg, jpeg.
                            </span>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            First Name*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <input
                                type="text"
                                placeholder="First name"
                                className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "firstname")}`}
                                name="firstname"
                                {...formik.getFieldProps("firstname")}
                            />
                            {formik.touched.firstname && formik.errors.firstname ? (
                                <div className="invalid-feedback">
                                    {formik.errors.firstname}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Last Name*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <input
                                type="text"
                                placeholder="Last name"
                                className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "lastname")}`}
                                name="lastname"
                                {...formik.getFieldProps("lastname")}
                            />
                            {formik.touched.lastname && formik.errors.lastname ? (
                                <div className="invalid-feedback">{formik.errors.lastname}</div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Email Address*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <span className="svg-icon menu-icon"> <SVG src={toAbsoluteUrl("/media/svg/icons/Communication/Mail-at.svg")} /> </span>
                                    </span>
                                </div>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "email")}`}
                                    name="email"
                                    {...formik.getFieldProps("email")}
                                />
                            </div>
                            {formik.touched.email && formik.errors.email ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.email}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Contact Phone*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <span className="svg-icon menu-icon"> <SVG src={toAbsoluteUrl("/media/svg/icons/Communication/Adress-book1.svg")} /> </span>                                    </span>
                                </div>
                                <InputMask
                                    mask="(999) 999-9999"
                                    type="text"
                                    placeholder="(225)-000-0000"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "phoneNumber")}`}
                                    name="phoneNumber"
                                    {...formik.getFieldProps("phoneNumber")}
                                />
                            </div>
                            {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.phoneNumber}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Date of Birth*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-calendar"></i>
                                    </span>
                                </div>
                                <DatePicker
                                    value={formik.getFieldProps("dateOfBirth").value ? formatToUSDate(formik.getFieldProps("dateOfBirth").value) : null}
                                    dateFormat="MM/dd/yyyy"
                                    selected={formik.getFieldProps("dateOfBirth").value ? new Date(formik.getFieldProps("dateOfBirth").value) : null}
                                    customInput={
                                        <MaskedInput
                                            className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "dateOfBirth")}`}
                                            mask="11/11/1111"
                                            placeholder="MM/DD/YYYY"
                                        />
                                    }
                                    onChange={(date) => {
                                        formik.setFieldValue("dateOfBirth", formatToUSDate(date))
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value.includes('_') || e.target.value === "") {
                                            formik.setFieldValue("dateOfBirth", '')
                                        }

                                        formik.setFieldTouched('dateOfBirth');
                                    }}
                                />
                            </div>
                            {formik.touched.dateOfBirth && formik.errors.dateOfBirth ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.dateOfBirth}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Driver License Number
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-id-card"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="8565424"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "driverLicenseNumber")}`}
                                    name="driverLicenseNumber"
                                    {...formik.getFieldProps("driverLicenseNumber")}
                                />
                            </div>
                            {formik.touched.driverLicenseNumber && formik.errors.driverLicenseNumber ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.driverLicenseNumber}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            SSN
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-shield-alt"></i>
                                    </span>
                                </div>
                                <InputMask
                                    mask="999-99-9999"
                                    placeholder="123-45-6789"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "ssn")}`}
                                    name="ssn"
                                    {...formik.getFieldProps("ssn")}
                                />
                            </div>
                            {formik.touched.ssn && formik.errors.ssn ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.ssn}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Signature
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <FilePond
                                labelIdle='Drag & Drop Signature Image of this User or <span class="filepond--label-action">Browse</span>'
                                onupdatefiles={setSignatureFile}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-xl-3"></label>
                        <div className="col-lg-9 col-xl-6">
                            <h5 className="mt-lg-10 mb-6 fz-md fw-700 c-primary">Account Info</h5>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Username*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-user"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="JohnDoe07"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "username")}`}
                                    name="username"
                                    {...formik.getFieldProps("username")}
                                />
                            </div>
                            {formik.touched.username && formik.errors.username ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.username}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Password*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-lock"></i>
                                    </span>
                                </div>
                                <input
                                    type="password"
                                    placeholder="*********"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "password")}`}
                                    name="password"
                                    {...formik.getFieldProps("password")}
                                />
                            </div>
                            {formik.touched.password && formik.errors.password ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.password}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Confirm Password*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-lock-open"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="*********"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "confirmPassword")}`}
                                    name="confirmPassword"
                                    {...formik.getFieldProps("confirmPassword")}
                                />
                            </div>
                            {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.confirmPassword}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-xl-3"></label>
                        <div className="col-lg-9 col-xl-6">
                            <h5 className="mt-lg-10 mb-6 fz-md fw-700 c-primary">Company Info</h5>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Role*
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="checkbox-list">
                                <label className="checkbox checkbox-primary">
                                    <input
                                        type="checkbox"
                                        name="Admin"
                                        onChange={handleRoleChange}
                                    />
                                    <span></span> Administrator
                                </label>
                                <label className="checkbox checkbox-primary">
                                    <input
                                        type="checkbox"
                                        name="Project Manager"
                                        onChange={handleRoleChange}
                                    />
                                    <span></span> Project Manager
                                </label>
                                <label className="checkbox checkbox-primary">
                                    <input
                                        type="checkbox"
                                        name="Technician"
                                        onChange={handleRoleChange}
                                    />
                                    <span></span> Technician
                                </label>
                                <label className="checkbox checkbox-primary">
                                    <input
                                        type="checkbox"
                                        name="Worker"
                                        onChange={handleRoleChange}
                                    />
                                    <span></span> Worker
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Hourly Rate
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-dollar-sign"></i>
                                    </span>
                                </div>
                                <input
                                    type="number"
                                    placeholder="14.99"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "hourlyRate")}`}
                                    name="hourlyRate"
                                    {...formik.getFieldProps("hourlyRate")}
                                />
                            </div>
                            {formik.touched.hourlyRate && formik.errors.hourlyRate ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.hourlyRate}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Payload Multiplier
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-times"></i>
                                    </span>
                                </div>
                                <input
                                    type="number"
                                    placeholder="0.00"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "payloadMultiplier")}`}
                                    name="payloadMultiplier"
                                    {...formik.getFieldProps("payloadMultiplier")}
                                />
                            </div>
                            {formik.touched.payloadMultiplier && formik.errors.payloadMultiplier ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.payloadMultiplier}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            TWIC Pin#
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-hashtag"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="9985648"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "TWICPin")}`}
                                    name="TWICPin"
                                    {...formik.getFieldProps("TWICPin")}
                                />
                            </div>
                            {formik.touched.TWICPin && formik.errors.TWICPin ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.TWICPin}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Safety Council Student ID
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-id-badge"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="778564"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "safetyCouncilStudentId")}`}
                                    name="safetyCouncilStudentId"
                                    {...formik.getFieldProps("safetyCouncilStudentId")}
                                />
                            </div>
                            {formik.touched.safetyCouncilStudentId && formik.errors.safetyCouncilStudentId ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.safetyCouncilStudentId}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            DISA ID #
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-id-card-alt"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="001234"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "DISAId")}`}
                                    name="DISAId"
                                    {...formik.getFieldProps("DISAId")}
                                />
                            </div>
                            {formik.touched.DISAId && formik.errors.DISAId ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.DISAId}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            AI #
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-hashtag"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="0012345"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "AIId")}`}
                                    name="AIId"
                                    {...formik.getFieldProps("AIId")}
                                />
                            </div>
                            {formik.touched.AIId && formik.errors.AIId ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.AIId}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Is Active?
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="checkbox-list">
                                <label className="checkbox checkbox-primary">
                                    <input
                                        checked={formik.values.isActive}
                                        type="checkbox"
                                        name="admin"
                                        {...formik.getFieldProps("isActive")}
                                    />
                                    <span></span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <label className="col-xl-3"></label>
                        <div className="col-lg-9 col-xl-6">
                            <h5 className="mt-lg-10 mb-6 fz-md fw-700 c-primary">Address Info</h5>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Address 1
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-home"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="123 Second St."
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "address1")}`}
                                    name="address1"
                                    {...formik.getFieldProps("address1")}
                                />
                            </div>
                            {formik.touched.address1 && formik.errors.address1 ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.address1}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Address 2
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-building"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Apt# 1"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "address2")}`}
                                    name="address2"
                                    {...formik.getFieldProps("address2")}
                                />
                            </div>
                            {formik.touched.address2 && formik.errors.address2 ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.address2}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            City
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-city"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Baton Rouge"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "city")}`}
                                    name="city"
                                    {...formik.getFieldProps("city")}
                                />
                            </div>
                            {formik.touched.city && formik.errors.city ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.city}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            State
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-flag-usa"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Louisiana"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "state")}`}
                                    name="state"
                                    {...formik.getFieldProps("state")}
                                />
                            </div>
                            {formik.touched.state && formik.errors.state ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.state}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600">
                            Zip Code
                        </label>
                        <div className="col-lg-9 col-xl-6">
                            <div className="input-group input-group-lg input-group-solid">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">
                                        <i className="fa fa-location-arrow"></i>
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    placeholder="70809"
                                    className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, "zipCode")}`}
                                    name="zipCode"
                                    {...formik.getFieldProps("zipCode")}
                                />
                            </div>
                            {formik.touched.zipCode && formik.errors.zipCode ? (
                                <div className="invalid-feedback display-block">
                                    {formik.errors.zipCode}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
}

export default connect(null, alert.actions)(UserRegisterPage);